import { consentRoleLabel, consentRolesFromData, findRoleByLabel, getSignerRoleLabel, hasUnreadableStoredRoles, isNonSigningSignerType, readableConsentRolesFromData, signingOrderFromRoles, signingOrders, } from 'libs.consent.common';
import { CONSENT_ELEMENT_TYPES } from '../../constants/consent_element_types.constants';
const TEXT_INPUT_KIND_LABEL = {
    ec__text: 'Text',
    ec__numeric: 'Numeric',
    ec__email: 'Email',
};
/** Overlay / inspector name for a placed field. Never the generated `ec__title` id. */
export const elementFieldDisplayName = (element) => {
    if (element.type === 'TEXT_INPUT') {
        return TEXT_INPUT_KIND_LABEL[element.config.ec__input_type ?? ''] ?? 'Text';
    }
    return CONSENT_ELEMENT_TYPES[element.type].label;
};
const requestedDataTypeByKey = (data) => {
    const rows = data.ec__requested_data;
    const map = new Map();
    if (!Array.isArray(rows)) {
        return map;
    }
    for (const row of rows) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const record = row;
        if (typeof record.ec__key === 'string' && typeof record.ec__type === 'string') {
            map.set(record.ec__key, record.ec__type);
        }
    }
    return map;
};
const elementsWithRequestedTypes = (elements, data) => {
    const types = requestedDataTypeByKey(data);
    if (types.size === 0) {
        return elements;
    }
    return elements.map((element) => {
        const fromRequested = types.get(element.config.ec__key ?? element.id);
        if (element.type !== 'TEXT_INPUT' ||
            !(fromRequested && fromRequested in TEXT_INPUT_KIND_LABEL) ||
            fromRequested === element.config.ec__input_type) {
            return element;
        }
        return {
            ...element,
            config: { ...element.config, ec__input_type: fromRequested },
        };
    });
};
const optionalNonEmptyString = (value) => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};
const firstString = (...values) => {
    for (const value of values) {
        const next = optionalNonEmptyString(value);
        if (next) {
            return next;
        }
    }
    return undefined;
};
const actorName = (value) => {
    if (typeof value === 'string') {
        return optionalNonEmptyString(value);
    }
    if (value && typeof value === 'object') {
        const record = value;
        return firstString(record.name, record.displayName, record.email, record.id);
    }
    return undefined;
};
const libraryStatusToDisplay = (status) => {
    switch (status) {
        case 'PUBLISHED':
            return 'Published';
        case 'IN_REVIEW':
            return 'In Review';
        case 'ARCHIVED':
            return 'Archived';
        default:
            return 'Draft';
    }
};
export const sourceMetadataFromResource = (data, metadata) => ({
    sourceFileName: firstString(data.sourceFileName, data.fileName, metadata.sourceFileName, metadata.fileName),
    sourceLabel: firstString(data.sourceLabel, data.ec__source_label, metadata.sourceLabel, metadata.ec__source_label),
    sourceVersion: firstString(data.sourceVersion, data.ec__source_version, metadata.sourceVersion, metadata.ec__source_version),
    sourceVersionDate: firstString(data.sourceVersionDate, data.ec__source_version_date, metadata.sourceVersionDate, metadata.ec__source_version_date),
});
export const actorsFromResource = (resource) => ({
    createdBy: actorName(resource.createdBy),
    updatedBy: actorName(resource.updatedBy),
});
export const documentFromTemplateResource = (resource) => {
    const data = resource.data ?? {};
    const meta = resource.metadata ?? {};
    const storedDataInvalid = hasUnreadableStoredRoles(data);
    return {
        id: resource.id,
        key: resource.key,
        name: resource.name,
        language: firstString(data.ec__language, data.language),
        description: typeof data.description === 'string' ? data.description : '',
        status: libraryStatusToDisplay(resource.status),
        assetId: typeof meta.consentAssetId === 'string' ? meta.consentAssetId : '',
        pdfDataUrl: '',
        totalPages: typeof data.totalPages === 'number' ? data.totalPages : 0,
        elements: elementsWithRequestedTypes(Array.isArray(data.elements) ? data.elements : [], data),
        roles: storedDataInvalid ? readableConsentRolesFromData(data) : consentRolesFromData(data),
        ...(storedDataInvalid && { storedDataInvalid }),
        customFields: Array.isArray(data.customFields) ? data.customFields : [],
        createdAt: resource.createdAt,
        updatedAt: resource.updatedAt,
    };
};
export const formatConfigurationTimestamp = (value) => {
    if (!value) {
        return '';
    }
    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) {
        return '';
    }
    return new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(new Date(timestamp));
};
export const displayLanguage = (language) => {
    if (!language) {
        return '';
    }
    return language.replace('_', '-');
};
export const roleConfigurationRows = (roles) => {
    const orders = signingOrders(roles);
    const counts = new Map();
    for (const order of orders) {
        if (order > 0) {
            counts.set(order, (counts.get(order) ?? 0) + 1);
        }
    }
    return roles.map((role, index) => {
        const order = orders[index] ?? 0;
        return {
            id: role.id || `role-${index}`,
            order,
            displayName: consentRoleLabel(role),
            signerTypeLabel: getSignerRoleLabel(role.signerTypeId),
            required: role.roleRequired,
            isParallel: order > 0 && (counts.get(order) ?? 0) > 1,
            isNonSigning: isNonSigningSignerType(role.signerTypeId),
        };
    });
};
export const elementConfigurationRows = (elements, roles) => [...elements]
    .sort((left, right) => left.page - right.page)
    .map((element) => {
    const assignedRole = element.config.ec__signer_role ?? '';
    const matched = findRoleByLabel(assignedRole, roles);
    const unmatchedNonSigning = matched ? isNonSigningSignerType(matched.signerTypeId) : false;
    return {
        id: element.id,
        ecKey: element.config.ec__key ?? element.id,
        type: element.type,
        page: element.page,
        assignedRole,
        signingOrder: signingOrderFromRoles(assignedRole, roles) ?? 0,
        unresolvedRole: Boolean(assignedRole) && (!matched || unmatchedNonSigning),
        fieldName: elementFieldDisplayName(element),
        label: element.config.ec__label ?? '',
        description: element.config.ec__description ?? '',
        optional: Boolean(element.config.ec__optional),
        capturesInitials: element.type === 'INITIALS',
        choiceOptions: (element.config.ec__options ?? []).map((option) => ({
            key: option.key,
            value: option.value,
        })),
        configurationData: Array.isArray(element.config.ec__custom_data) ? element.config.ec__custom_data : [],
    };
});
